import { deepEqual } from 'fast-equals';
import { Formik } from 'formik';
import { useSnackbar } from 'notistack';
import { FC, useEffect, useState } from 'react';
import * as Yup from 'yup';
// Components
import { ImageModal, Loader, Modal } from '../../../components';
import {
  createTerritory,
  createTerritoryImages,
  getDomainUsers,
  getTerritoryPersonnelTypes,
  updateTerritory
} from '../../../fetch';
import { formatMoney } from '../../../helpers';
import { IDomainUser, IDropdownResponse, ITerritoryDetail } from '../../../models';
import { TerritoryModalForm } from './TerritoryModalForm';

interface ITerritoryModal {
  open: boolean;
  onClose: () => void;
  currentTerritory?: ITerritoryDetail;
  fetchTerritories: () => {};
}

const TerritorySchema = Yup.object().shape({
  name: Yup.string().max(255, 'Max 255 characters').required('Required'),
  description: Yup.string().max(3000, 'Max 3000 characters'),
  manager: Yup.string().max(255, 'Max 255 characters'),
  buildDelay: Yup.string(),
  startsPerWeek: Yup.string(),
  annualSalesGoal: Yup.string(),
  personnel: Yup.array(
    Yup.object({
      userName: Yup.string(),
      personnelType: Yup.string(),
      isActive: Yup.boolean()
    })
  ),
  images: Yup.array(Yup.string())
});

export const TerritoryModal: FC<ITerritoryModal> = ({
  open,
  onClose,
  currentTerritory,
  fetchTerritories
}) => {
  const { enqueueSnackbar } = useSnackbar();
  const [actualSalesGoal, setActualSalesGoal] = useState<number>(0);
  const [imagesFiles, setImageFiles] = useState<File[] | null>(null);
  const [currentImage, setCurrentImage] = useState<string | null>(null);
  const [isShowingImage, showImage] = useState(false);
  const [isLoadingTypes, setLoadingTypes] = useState(true);
  const [isLoadingUsers, setLoadingUsers] = useState(true);
  const [selectedType, setSelectedType] = useState<string>('');
  const [selectedName, setSelectedName] = useState<string>('');
  const [selectedUserName, setSelectedUserName] = useState<string>('');
  const [personnelTypes, setPersonnelTypes] = useState<IDropdownResponse[]>([]);
  const [users, setUsers] = useState<IDomainUser[]>([]);

  useEffect(() => {
    if (currentTerritory) {
      setActualSalesGoal(currentTerritory.annualSalesGoal);
    }
  }, [currentTerritory]);

  const fetchTerritoryPersonnelNames = async () => {
    try {
      const res = await getDomainUsers({ excludeTerritoryId: currentTerritory?.territoryId });
      setUsers(res.records);
    } catch (error) {
      enqueueSnackbar('Error loading personnel names, please try again.', {
        variant: 'error'
      });
    } finally {
      setLoadingUsers(false);
    }
  };

  const fetchTerritoryPersonnelTypes = async () => {
    try {
      const res = await getTerritoryPersonnelTypes();
      setPersonnelTypes(res);
    } catch (error) {
      enqueueSnackbar('Error loading personnel types, please try again.', {
        variant: 'error'
      });
    } finally {
      setLoadingTypes(false);
    }
  };

  useEffect(() => {
    fetchTerritoryPersonnelTypes();
    fetchTerritoryPersonnelNames();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <>
      <Formik
        enableReinitialize={true}
        initialValues={{
          name: currentTerritory?.name ?? '',
          description: currentTerritory?.description ?? '',
          manager: currentTerritory?.manager ?? '',
          buildDelay: currentTerritory?.buildDelay ?? '',
          startsPerWeek: currentTerritory?.startsPerWeek ?? '',
          annualSalesGoal: currentTerritory?.annualSalesGoal
            ? formatMoney(currentTerritory.annualSalesGoal)
            : '',
          personnel: currentTerritory?.personnel ?? [],
          images: currentTerritory?.images ?? []
        }}
        validationSchema={TerritorySchema}
        onSubmit={async (values, actions) => {
          try {
            const handleImages = id => {
              if (imagesFiles && imagesFiles.length > 0) {
                imagesFiles.forEach(async imageFile => {
                  const formData = new FormData();
                  formData.append('image', imageFile);
                  await createTerritoryImages(id, formData);
                });
              }
            };
            let res;
            const data = {
              ...values,
              isActive: true,
              annualSalesGoal: Number(actualSalesGoal),
              buildDelay: Number(values.buildDelay),
              startsPerWeek: Number(values.startsPerWeek)
            };
            delete data.images;
            if (currentTerritory && currentTerritory.territoryId) {
              res = await updateTerritory(currentTerritory.territoryId, data);
              handleImages(currentTerritory.territoryId);
            } else {
              res = await createTerritory(data);
              if (res && !res.Detail) {
                handleImages(res);
              }
            }
            if (res && res.invalidPersonnel && res.invalidPersonnel.length) {
              enqueueSnackbar(
                `The following users have been removed because they are no longer active: ${res.invalidPersonnel
                  .map(name => name)
                  .join(', ')}.`,
                {
                  variant: 'warning'
                }
              );
            }

            enqueueSnackbar(currentTerritory ? 'Territory Updated!' : 'Territory Created!', {
              variant: 'success'
            });
            onClose();
            fetchTerritories();
            // reset state
            setImageFiles(null);
            actions.resetForm();
          } catch (error) {
            enqueueSnackbar(error?.Detail ?? 'Error saving territory, please try again.', {
              variant: 'error'
            });
          }
        }}
      >
        {({
          resetForm,
          isSubmitting,
          values,
          initialValues,
          setFieldValue,
          errors,
          touched,
          handleSubmit,
          dirty,
          isValid,
          handleBlur
        }) => {
          return (
            <Modal
              open={open}
              onClose={() => {
                if (!deepEqual(initialValues, values)) {
                  const result = window.confirm(
                    'You have unsaved changes, are you sure you want to exit?'
                  );
                  if (result) {
                    resetForm();
                    // reset state
                    setImageFiles(null);
                    onClose();
                  } else {
                    return;
                  }
                } else {
                  setImageFiles(null);
                  onClose();
                }
              }}
              maxWidth="md"
            >
              {isSubmitting && <Loader type="overlay" position="centered" />}

              <TerritoryModalForm
                open={open}
                currentTerritory={currentTerritory}
                handleSubmit={handleSubmit}
                values={values}
                handleBlur={handleBlur}
                errors={errors}
                touched={touched}
                setFieldValue={setFieldValue}
                setActualSalesGoal={setActualSalesGoal}
                users={users}
                isLoadingUsers={isLoadingUsers}
                personnelTypes={personnelTypes}
                isLoadingTypes={isLoadingTypes}
                initialValues={initialValues}
                onClose={onClose}
                showImage={showImage}
                setCurrentImage={setCurrentImage}
                imagesFiles={imagesFiles}
                setImageFiles={setImageFiles}
                selectedName={selectedName}
                setSelectedName={setSelectedName}
                selectedType={selectedType}
                setSelectedType={setSelectedType}
                selectedUserName={selectedUserName}
                setSelectedUserName={setSelectedUserName}
                dirty={dirty}
                isSubmitting={isSubmitting}
                isValid={isValid}
                resetForm={resetForm}
              />
            </Modal>
          );
        }}
      </Formik>
      <ImageModal
        isOpen={isShowingImage}
        source={currentImage}
        onClose={() => {
          setCurrentImage(null);
          showImage(false);
        }}
      />
    </>
  );
};
